import { Button, Modal, Table } from 'react-bootstrap'
import moment from 'moment'
import useOrganizationHubspotTickets, {
  HubspotTicket,
} from 'hooks/use-organization-hubspot-tickets'
import { User } from 'interfaces/user'
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import informationIcon from 'images/information-circle-outline.svg'

interface HubspotTicketsModalProps {
  show: boolean
  handleClose: () => void
  orgId: string
  internalAdmins: User[]
}

export const HubspotTicketsModal = ({
  show,
  handleClose,
  orgId,
  internalAdmins,
}: HubspotTicketsModalProps) => {
  const { data: hubspotTickets, refetch: fetchHubspotTickets } =
    useOrganizationHubspotTickets(orgId)

  const handleShow = () => {
    fetchHubspotTickets()
  }

  const formatTimeAgo = (dateString: string) => {
    return moment(dateString).calendar(null, {
      sameDay: '[today at] h:mm A',
      lastDay: '[yesterday at] h:mm A',
      lastWeek: 'dddd [at] h:mm A',
      sameElse: 'M/D/YYYY [at] h:mm A',
    })
  }

  const priorityColors = {
    low: '#07bca4', // green
    medium: '#ffc107', // yellow
    high: '#dc3545', // red
    urgent: '#dc3545', // red
  }

  const getPriorityColor = (priority: string) => {
    const normalizedPriority = priority?.toLowerCase()
    return (
      priorityColors[normalizedPriority as keyof typeof priorityColors] ||
      '#6c757d'
    ) // default gray
  }

  const getUserName = (userId: string | number) => {
    if (!userId || userId === 'N/A') return 'N/A'

    const user = internalAdmins.find((admin) => {
      if (admin.id.toString() === userId.toString()) return true
      return false
    })

    if (!user) {
      return 'N/A'
    }

    return user.name || 'N/A'
  }

  const [showTooltip, setShowTooltip] = useState(false)

  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => {
        setShowTooltip(false)
      }, 4000)

      return () => clearTimeout(timer)
    }
  }, [showTooltip])

  useEffect(() => {
    const handleClickOutside = () => {
      setShowTooltip(false)
    }

    if (showTooltip) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [showTooltip])

  return (
    <Modal show={show} onHide={handleClose} onShow={handleShow} size="xl">
      <Modal.Header>
        <Modal.Title>Hubspot Tickets</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {hubspotTickets && hubspotTickets.hubspot_tickets.length > 0 ? (
          <Table striped bordered hover responsive variant="dark">
            <thead>
              <tr>
                <th>Ticket Info</th>
                <th>Ticket Link</th>
                <th>
                  <div className="d-flex align-items-center position-relative">
                    Requested User
                    {hubspotTickets?.hubspot_tickets?.some(
                      (ticket) => !ticket.ticket_requested_by
                    ) && (
                      <img
                        src={informationIcon}
                        className="ms-2"
                        style={{
                          cursor: 'pointer',
                          width: '20px',
                          height: '20px',
                          transition: 'transform 0.2s ease',
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          setShowTooltip(!showTooltip)
                        }}
                        onMouseEnter={(e) => {
                          ;(e.target as HTMLElement).style.transform =
                            'scale(1.1)'
                        }}
                        onMouseLeave={(e) => {
                          ;(e.target as HTMLElement).style.transform =
                            'scale(1)'
                        }}
                        alt="Information"
                      />
                    )}
                    {showTooltip &&
                      hubspotTickets?.hubspot_tickets?.some(
                        (ticket) => !ticket.ticket_requested_by
                      ) && (
                        <div
                          className="position-absolute text-white p-3"
                          style={{
                            top: '100%',
                            left: '0',
                            fontSize: '0.875rem',
                            maxWidth: 'auto',
                            zIndex: 1000,
                            backgroundColor: '#000',
                            borderRadius: '10px',
                            marginTop: '5px',
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <p className="mb-0">
                            User name will show after a user claims the number
                            in the Linq app. Empty if unclaimed.
                          </p>
                          <div
                            className="position-absolute"
                            style={{
                              bottom: '100%',
                              left: '50px',
                              border: '8px solid transparent',
                              borderBottomColor: '#000',
                            }}
                          />
                        </div>
                      )}
                  </div>
                </th>
                <th>Assigned Users</th>
                <th>Zip Code</th>
                <th>Area Code</th>
                <th>Opened</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {hubspotTickets.hubspot_tickets.map((ticket: HubspotTicket) => (
                <tr key={ticket.id}>
                  <td>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                      }}
                    >
                      <div>
                        <strong>Type:</strong>{' '}
                        {ticket.ticket_type === '0' ? (
                          <span style={{ color: '#4fa7ff' }}>Blue Number</span>
                        ) : (
                          '-'
                        )}
                      </div>
                      <div>
                        <strong>Status:</strong> {ticket.ticket_status || '-'}
                      </div>
                      <div>
                        <strong>Priority:</strong>{' '}
                        <span
                          style={{
                            color: getPriorityColor(ticket.ticket_priority),
                          }}
                        >
                          {ticket.ticket_priority || '-'}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    {ticket.external_id ? (
                      <a
                        href={`https://app.hubspot.com/contacts/7157564/record/0-5/${ticket.external_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#4fa7ff',
                          textDecoration: 'underline',
                          fontSize: '14px',
                        }}
                      >
                        Go to HubSpot
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {ticket.ticket_requested_by ? (
                      <Link
                        to={`/user/${ticket.ticket_requested_by}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#4fa7ff',
                          textDecoration: 'underline',
                          fontSize: '14px',
                        }}
                      >
                        {getUserName(ticket.ticket_requested_by)}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                      }}
                    >
                      <div>
                        <strong>Sales Rep:</strong>{' '}
                        <span style={{ color: '#4fa7ff' }}>
                          {getUserName(
                            ticket.sales_representative_linq_user_id
                          ) || '-'}
                        </span>
                      </div>
                      <div>
                        <strong>Account Manager:</strong>{' '}
                        <span style={{ color: '#4fa7ff' }}>
                          {getUserName(ticket.ticket_owner_linq_user_id) || '-'}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>{ticket.reqzipcode || '-'}</td>
                  <td>{ticket.area_code || '-'}</td>
                  <td>
                    {ticket.opened_at ? formatTimeAgo(ticket.opened_at) : '-'}
                  </td>
                  <td>
                    {ticket.updated_at ? formatTimeAgo(ticket.updated_at) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div className="text-center py-4">
            <p>No Hubspot tickets found.</p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
